import './index.scss'
import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import StarSvg from './images/star.svg?react'
import { message } from 'antd'
import {
  AlipayCircleFilled,
  CloseOutlined,
  WechatFilled
} from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'
import { usePaymentOrder } from '../VipPaymentModal/hooks/usePaymentOrder'
import { rechargeAgentPoints } from '../../../actions/payment'
import paymentConfig from '../../../constants/payment'
import { POINTS_PACK_CONFIG_KEY, parsePointsPackConfig } from './config'
import type { PointsPackItem } from './types'

interface Props {
  /** 是否显示（受控） */
  visible: boolean
  /** 关闭回调 */
  onClose: () => void
  /** 打开来源（埋点用，透传给下单 sourceDetail / paySource） */
  source?: string
  /**
   * 加购支付成功回调。orderNo=订单号，pack=当时选中的套餐。
   * 在这里做埋点 / 刷新用户积分 / 改业务状态等。
   * 返回 Promise 时，弹窗会等它完成再走后续（刷新团队信息 + 自动关闭）。
   */
  onPaymentSuccess?: (
    orderNo: string,
    pack: PointsPackItem | null
  ) => Promise<void> | void
  /** 选择套餐回调（埋点） */
  onSelectPack?: (pack: PointsPackItem) => void
  /** 切换支付方式回调（埋点）；channel 为 paymentConfig.channelType 值, pack 为当前选中套餐 */
  onChannelChange?: (channel: number, pack: PointsPackItem | null) => void
  /** 弹窗曝光回调（埋点）；打开并确定默认选中套餐后触发一次, 传默认套餐 */
  onExpose?: (defaultPack: PointsPackItem | null) => void
  /** 点《会员协议》回调（埋点） */
  onAgreementClick?: () => void
  /** 点「二维码已失效，点击刷新」回调（埋点）。
   *  单列出来是因为切支付方式也会重建二维码(createOrder(true)),
   *  二者无法靠 sensorClickEvent 的事件名区分, 故用专门回调精确上报刷新。 */
  onRefreshQRCode?: () => void
  /** 通用点击埋点，透传给 usePaymentOrder（如刷新二维码 refreshAliQRcode） */
  sensorClickEvent?: (evt: string) => void
}

/** 底部权益条（固定文案） */
const BENEFITS = ['永久有效', '团队共享', '不限期使用', '即买即用']
const { WeChat, Alipay } = paymentConfig.channelType

/**
 * 积分加量包购买弹窗（通用公共组件，受控）。
 *
 * - 套餐来自 KV 配置（POINTS_PACK_CONFIG_KEY），启动时随 CONFIG.KVParams.names
 *   一起拉到 Redux，这里按 key 取出解析；未配置回退默认文案。
 * - 支付复用会员弹窗那套：usePaymentOrder 下单 + 轮询，PaymentQRCode 渲染二维码。
 *   下单只认商品 id，把选中套餐构造成 { id: goodsId, price } 传进去即可。
 *
 * 用法：<PointsPackModal visible={x} onClose={...} source="aiFormFillerBuyCredits" />
 */
const PointsPackModal: React.FC<Props> = ({
  visible,
  onClose,
  source,
  onPaymentSuccess,
  onSelectPack,
  onChannelChange,
  onExpose,
  onAgreementClick,
  onRefreshQRCode,
  sensorClickEvent
}) => {
  const dispatch = useAppDispatch()
  // 充值归属团队的长 ID（GroupIdReal）；与 usePaymentOrder 下单用的 groupID 同源
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const rawConfig = useAppSelector(
    (state: any) => state.main.config?.[POINTS_PACK_CONFIG_KEY]
  )
  const config = useMemo(() => parsePointsPackConfig(rawConfig), [rawConfig])

  const [selectedKey, setSelectedKey] = useState('')

  // 打开时默认选中「推荐」套餐（没有则第一个），并把默认套餐回调给曝光埋点
  useEffect(() => {
    if (!visible) return
    const defaultPack =
      config.packages.find((p) => p.recommended) || config.packages[0] || null
    setSelectedKey(defaultPack?.key || '')
    // 仅在已拿到套餐(KV 配置就绪)时报曝光: KV 异步加载会让本 effect 先空跑一次、
    // 配置到位再跑一次, 空那次跳过, 避免曝光双报 + string1 上报空串。
    if (onExpose && defaultPack) onExpose(defaultPack)
    // onExpose 不进依赖, 避免调用方内联函数导致重复曝光
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, config])

  const selectedPack =
    config.packages.find((p) => p.key === selectedKey) || null

  // 下单只认商品 id；价格用于二维码区展示
  const selectedGoods = useMemo(
    () =>
      selectedPack
        ? { id: selectedPack.goodsId, price: selectedPack.price }
        : null,
    [selectedPack]
  )

  const {
    alipayUrl,
    wechatUrl,
    qrCodeInvalid,
    channel,
    setChannel,
    isCreatingOrder,
    createOrder
  } = usePaymentOrder(selectedGoods, {
    paySource: source,
    sensorClickEvent,
    onClose,
    // 支付成功 → 调主站 /web/agent/recharge 给团队充「永久积分」（组件固有逻辑，必做）。
    // points 由订单规格自动算，group_id 用下单同款长 ID；到账后再触发外漏回调让调用方刷新余额。
    onPaymentSuccess: async (orderNo: string) => {
      try {
        const res: any = await dispatch(
          rechargeAgentPoints({
            group_id: groupID,
            order_no: orderNo,
            remark: selectedPack
              ? `购买${selectedPack.points}积分加量包`
              : undefined
          })
        )
        const code = res && res.code
        // 200 充值成功 / 45002 已充值过（幂等，视为到账）
        if (code === 200 || code === 45002) {
          message.success('积分已到账')
          if (onPaymentSuccess) onPaymentSuccess(orderNo, selectedPack)
        } else {
          message.error(
            (res && res.msg) || '积分充值失败，请稍后重试或联系客服'
          )
        }
      } catch (e) {
        message.error('积分充值失败，请稍后重试或联系客服')
      }
    }
  })

  const switchChannel = (c: number) => {
    if (c === channel) return
    setChannel(c)
    createOrder(true)
    if (onChannelChange) onChannelChange(c, selectedPack)
  }

  if (!visible) return null

  const isWechat = channel === WeChat
  const qrUrl = isWechat ? wechatUrl : alipayUrl

  // Portal 到 body：脱离 mainWrapper 全局 <Spin> 的 children，避免下单触发全局
  // loading 时整个弹窗被 .ant-spin-blur 半透明化、透出底层 Modal 造成闪烁
  return ReactDOM.createPortal(
    <div className="points-pack-mask" onClick={onClose}>
      <div className="points-pack-modal" onClick={(e) => e.stopPropagation()}>
        <StarSvg className="pp-star" aria-hidden="true" />
        <div className="pp-header">
          <span className="pp-header-title">购买积分加量包</span>
          <span className="pp-header-tag">永久有效</span>
          <CloseOutlined
            className="pp-close"
            onClick={onClose}
            style={{ color: 'rgba(0,0,0,0.65)' }}
          />
        </div>

        <div className="pp-body">
          <div className="pp-hero">
            <span className="pp-hero-title">{config.title}</span>
            <span className="pp-hero-suffix">{config.titleSuffix}</span>
          </div>

          <div className="pp-packages">
            {config.packages.map((pack) => (
              <PackageCard
                key={pack.key}
                pack={pack}
                active={pack.key === selectedKey}
                onSelect={() => {
                  setSelectedKey(pack.key)
                  if (onSelectPack) onSelectPack(pack)
                }}
              />
            ))}
          </div>

          <div className="pp-benefits-bar">
            {BENEFITS.map((t, i) => (
              <React.Fragment key={t}>
                {i > 0 && <span className="pp-benefits-dot">·</span>}
                <span className="pp-benefits-item">✓ {t}</span>
              </React.Fragment>
            ))}
          </div>

          {/* 支付区（横向：支付方式竖列 | 二维码 | 价格），逻辑复用 usePaymentOrder */}
          <div className="pp-payment">
            <div className="pp-pay-methods">
              <button
                type="button"
                className={`pp-pay-method${!isWechat ? ' active' : ''}`}
                onClick={() => switchChannel(Alipay)}
              >
                <AlipayCircleFilled className="pp-pay-icon alipay" />
                支付宝
              </button>
              <button
                type="button"
                className={`pp-pay-method${isWechat ? ' active' : ''}`}
                onClick={() => switchChannel(WeChat)}
              >
                <WechatFilled className="pp-pay-icon wechat" />
                微信
              </button>
            </div>

            <div className="pp-pay-qr">
              {qrCodeInvalid ? (
                <div
                  className="pp-pay-qr-tip"
                  onClick={() => {
                    if (onRefreshQRCode) onRefreshQRCode()
                    createOrder(true)
                  }}
                >
                  二维码已失效，点击刷新
                </div>
              ) : isCreatingOrder || !qrUrl ? (
                <div className="pp-pay-qr-tip">
                  {isCreatingOrder ? '二维码生成中…' : '选择套餐后生成'}
                </div>
              ) : isWechat ? (
                <img src={qrUrl} alt="微信支付二维码" />
              ) : (
                <div className="pp-pay-qr-alipay">
                  <div className="pp-pay-qr-alipay-inner">
                    <iframe title="alipay" scrolling="no" src={qrUrl} />
                  </div>
                </div>
              )}
            </div>

            <div className="pp-pay-info">
              <div className="pp-pay-price">
                <span className="pp-pay-price-cur">¥</span>
                <span className="pp-pay-price-num">
                  {selectedPack?.price ?? ''}
                </span>
              </div>
              <div className={`pp-pay-scan${isWechat ? ' wechat' : ''}`}>
                {isWechat ? (
                  <WechatFilled className="pp-pay-scan-icon" />
                ) : (
                  <AlipayCircleFilled className="pp-pay-scan-icon" />
                )}
                {isWechat ? '微信扫码支付' : '支付宝扫码支付'}
              </div>
            </div>
          </div>

          <div className="pp-agreement">
            开通前请查阅{' '}
            <span
              className="pp-agreement-link"
              onClick={() => {
                if (onAgreementClick) onAgreementClick()
                window.open(
                  'https://www.xhey.top/x-vip_contract?app-hide-actions=1&version=2.2'
                )
              }}
            >
              《会员协议》
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

/** 单个套餐卡 */
const PackageCard: React.FC<{
  pack: PointsPackItem
  active: boolean
  onSelect: () => void
}> = ({ pack, active, onSelect }) => (
  <div className={`pp-card${active ? ' active' : ''}`} onClick={onSelect}>
    {pack.tag && <span className="pp-card-tag">{pack.tag}</span>}
    <div className="pp-card-name">{pack.name}</div>
    <div className="pp-card-points">
      <span className="pp-card-points-num">{pack.points}</span>
      <span className="pp-card-points-unit"> 积分</span>
    </div>
    <div className="pp-card-divider" />
    <div className="pp-card-price">
      <span className="pp-card-price-cur">¥</span>
      <span className="pp-card-price-num">{pack.price}</span>
    </div>
    {pack.queries && (
      <div className="pp-card-feat">
        <span className="pp-card-feat-icon">💬</span>
        {pack.queries}
      </div>
    )}
    {pack.hours && (
      <div className="pp-card-feat">
        <span className="pp-card-feat-icon">⏱</span>
        {pack.hours}
      </div>
    )}
    {active && <span className="pp-card-check">✓</span>}
  </div>
)

export default PointsPackModal
